import { MapContainer, useMap, Marker, TileLayer, useMapEvents } from "react-leaflet";
import React, { type FC, type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import L, { Icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import Settings from "@/Settings";
import type { Location } from "@/declarations/models/Location";
import StadiaAutocomplete from "@/components/Leaflet/StadiaAutocomplete";
import { IconButton, styled, Tooltip, Typography } from "@mui/material";
import { Help, WrongLocation } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import KPMarkerIconSVG from "@/assets/icons/marker_kp_solid_with_circle.svg";

export type KioLat = number;
export type KioLng = number;
export type KioLatLng = [KioLat, KioLng];

interface FitViewProps {
  mapControlRef: MutableRefObject<L.Map | null>;
  center: KioLatLng;
}

export const FitView: FC<FitViewProps> = ({ center, mapControlRef }) => {
  /*
   *   Fix problems with the map component being malformed when used together with collapse bars etc.
   * */
  const map = useMap();
  mapControlRef.current = map;

  useEffect(() => {
    const container = map.getContainer();
    let io: IntersectionObserver;
    // only call fit once
    const attemptFit = () => {
      const rect = container.getBoundingClientRect();
      const { x, y } = map.getSize();

      // 1) must have a real on‐screen size
      if (rect.width <= 0 || rect.height <= 0) return;
      map.invalidateSize();

      // cleanup
      io.disconnect();
    };

    io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          attemptFit();
        }
      },
      { threshold: 0.01 } // fire as soon as at least 1% is visible
    );
    io.observe(container);

    return () => {
      io.disconnect();
    };
  }, [map, center]);

  return null;
};

// Helper function to validate and normalize coordinates
const normalizeCoordinate = (value: number, type: "lat" | "lng"): number => {
  if (type === "lat") {
    return Math.max(-90, Math.min(90, value));
  } else {
    let normalized = value;
    while (normalized > 180) normalized -= 360;
    while (normalized < -180) normalized += 360;
    return normalized;
  }
};

const isValidCoordinate = (lat: number, lng: number): boolean => {
  return (
    !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
  );
};

interface MapEventsProps {
  onMapClick: (latlng: L.LatLng) => void;
}

const MapEvents: FC<MapEventsProps> = ({ onMapClick }) => {
  const map = useMap();

  useMapEvents({
    click: (e) => {
      const lat = normalizeCoordinate(e.latlng.lat, "lat");
      const lng = normalizeCoordinate(e.latlng.lng, "lng");

      if (isValidCoordinate(lat, lng)) {
        onMapClick(L.latLng(lat, lng));
      }
    },
  });

  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }, [map]);

  return null;
};

interface LocationPickerProps {
  location?: Location;
  width?: number;
  height?: number;
  onChange: (location: Location) => void;
}

export const LocationPickerDefaultZoom = 13;

const ButtonContainer = styled("div")`
  position: absolute;
  top: 50px;
  right: 10px;
  z-index: 1000;
`;

const HelpButtonContainer = styled("div")`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
`;

interface ResetPositionButtonProps {
  onResetPosition?: () => void;
}
const ResetPositionButton: FC<ResetPositionButtonProps> = ({ onResetPosition }) => {
  const { t } = useTranslation("common");

  return (
    <ButtonContainer>
      <Tooltip placement="left" title={t("components.locationPickerField.resetPosition")}>
        <IconButton onClick={onResetPosition} sx={{ color: "darkred" }} aria-label="reset gps position">
          <WrongLocation />
        </IconButton>
      </Tooltip>
    </ButtonContainer>
  );
};

const LocationPickerHelpButton: FC<ResetPositionButtonProps> = ({ onResetPosition }) => {
  const { t } = useTranslation("common");

  return (
    <HelpButtonContainer>
      <Tooltip
        placement="left"
        title={t("components.locationPickerField.mapHelpTooltip")
          .split("\\n")
          .map((line, i) => (
            <React.Fragment key={i}>
              {line}
              <br />
            </React.Fragment>
          ))}
      >
        <IconButton sx={{ color: "gray" }} aria-label="map tooltip">
          <Help />
        </IconButton>
      </Tooltip>
    </HelpButtonContainer>
  );
};

const KPMarkerIcon = new Icon({ iconUrl: `${KPMarkerIconSVG}`, iconSize: [40, 50], iconAnchor: [20, 45] });

const isLocationAValidPosition = (location: Location | null | undefined): boolean => {
  if (!location) return false;
  if (!location?.lat || !location?.lng) return false;
  if (Number.isNaN(location.lat) || Number.isNaN(location.lng)) return false;

  return true;
};

const LocationPicker: FC<LocationPickerProps> = ({ location, width, height, onChange }) => {
  const [position, setPosition] = useState<KioLatLng | null>(
    isLocationAValidPosition(location) && !!location ? [location.lat!, location.lng!] : null
  );
  const [resetAutocomplete, setResetAutocomplete] = useState<number>(0);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker<any> | null>(null);
  const get_default_position = (location: Location | null) => {
    return {
      lat: isLocationAValidPosition(location) ? location?.lat : Settings.LOCATION_PICKER_DEFAULT_COORDS.lat,
      lng: isLocationAValidPosition(location) ? location?.lng : Settings.LOCATION_PICKER_DEFAULT_COORDS.lng,
    };
  };

  useEffect(() => {
    if (location && location.lat && location.lng) {
      const markerPosition: KioLatLng = [location.lat, location.lng];
      setPosition(markerPosition);

      if (mapRef.current) {
        mapRef.current.setView(markerPosition);
      }
    }
  }, [location]);

  const defaultCenter: KioLatLng = useMemo(() => {
    const default_position = get_default_position(!!location ? location : null);
    return [default_position.lat!, default_position.lng!];
  }, [location]);

  const onChangeHandler = (position: KioLatLng): void => {
    const lat = normalizeCoordinate(position[0], "lat");
    const lng = normalizeCoordinate(position[1], "lng");
    onChange?.({ lat, lng });
  };

  const onResetPosition = () => {
    setPosition(null);
    setResetAutocomplete((prev) => prev + 1);
    onChange?.(undefined);
  };

  const mapOnClick = useCallback(
    (latlng: L.LatLng): void => {
      const lat = normalizeCoordinate(latlng.lat, "lat");
      const lng = normalizeCoordinate(latlng.lng, "lng");

      if (isValidCoordinate(lat, lng)) {
        const newPosition: KioLatLng = [lat, lng];
        setPosition(newPosition);
        setResetAutocomplete((prev) => prev + 1);
        onChange?.({ lat, lng }); // Already normalized sending directly to onChange
      }
    },
    [onChange]
  );

  // Change map cursor when holding Ctrl for better control
  useEffect(() => {
    const cursorChanger = (cursor: string) => {
      if (mapRef.current) {
        const leafletMapContainer = mapRef.current.getContainer();
        leafletMapContainer.style.cursor = cursor;
      }
      if (markerRef.current && markerRef.current.getElement()) {
        const leafletMarkerElement = markerRef.current.getElement();
        leafletMarkerElement.style.cursor = cursor;
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey) {
        cursorChanger("crosshair");
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      cursorChanger("");
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      cursorChanger("");
    };
  }, []);

  return (
    <div style={{ position: "relative" }}>
      <LocationPickerHelpButton />
      {!!position && <ResetPositionButton onResetPosition={onResetPosition} />}
      <StadiaAutocomplete
        position={position}
        setPosition={setPosition}
        mapControlRef={mapRef}
        autocompleteReset={resetAutocomplete}
        onChangeHandler={onChangeHandler}
      />
      <MapContainer
        crs={L.CRS.EPSG3857}
        style={{ width: "100%", height: 500 }}
        center={defaultCenter}
        zoom={LocationPickerDefaultZoom}
        minZoom={2}
        zoomSnap={0.1}
        zoomDelta={0.5}
        scrollWheelZoom={true}
        wheelPxPerZoomLevel={120}
      >
        <MapEvents onMapClick={mapOnClick} />
        {<FitView center={defaultCenter} mapControlRef={mapRef} />}
        {!!position && !!position[0] && !!position[1] && (
          <Marker ref={markerRef} icon={KPMarkerIcon} position={{ lat: position[0], lng: position[1] }} />
        )}

        <TileLayer url="https://tiles-eu.stadiamaps.com/styles/alidade_smooth/{z}/{x}/{y}{r}.png" />
      </MapContainer>
    </div>
  );
};

export default LocationPicker;
